'use strict';

/**
 * Card description panel (partners + laureates + governance blocks).
 *
 * Trigger wraps the image:
 *   - partners/laureates: <button class="f-partners__trigger">
 *   - governance: <div class="c-card__trigger" role="button" tabindex="0">
 *     (image is a <figure>, not allowed inside a native <button>).
 *
 * It carries aria-expanded + aria-controls and a unique aria-label. Click
 * (mouse, Enter, Space) toggles the panel; close cross and Escape close it.
 * Desktop mouse hover is a CSS-only preview. Closed panel = visibility:hidden
 * in CSS, so its content leaves the tab order and screen readers.
 */
const CARD_SELECTOR = '.f-partners__card, .c-card';
const TRIGGER_SELECTOR = '.f-partners__trigger, .c-card__trigger';
const CLOSE_SELECTOR = '.f-partners__panel-close, .c-card__panel-close';

const PanelCard = {
    init: function () {
        const cards = [...document.querySelectorAll(CARD_SELECTOR)]
            .filter(card => card.querySelector(TRIGGER_SELECTOR + ':not(.f-partners__trigger--static):not(.c-card__trigger--static)'));
        if (!cards.length) return;

        const setState = (card, open) => {
            card.classList.toggle('is-open', open);
            // Explicit open clears any prior Escape dismissal.
            if (open) card.classList.remove('is-dismissed');
            const trigger = card.querySelector(TRIGGER_SELECTOR);
            if (trigger) trigger.setAttribute('aria-expanded', String(open));
        };

        const closeOthers = (except) => {
            cards.forEach(card => {
                if (card !== except) setState(card, false);
            });
        };

        cards.forEach(card => {
            const trigger = card.querySelector(TRIGGER_SELECTOR);
            const closeBtn = card.querySelector(CLOSE_SELECTOR);
            if (!trigger) return;

            const toggle = () => {
                const willOpen = !card.classList.contains('is-open');
                closeOthers(card);
                setState(card, willOpen);
            };

            trigger.addEventListener('click', toggle);

            // role="button" (div): keyboard fires no native click.
            if (trigger.tagName !== 'BUTTON') {
                trigger.addEventListener('keydown', (e) => {
                    if (e.key === 'Enter' || e.key === ' ' || e.key === 'Spacebar') {
                        e.preventDefault();
                        toggle();
                    }
                });
            }

            // Decorative arrow (mobile): tap cue that toggles the card.
            // aria-hidden -> no duplicate for screen readers, which already
            // have the <button> around the image.
            const openIcon = card.querySelector('.f-partners__open');
            if (openIcon) {
                openIcon.addEventListener('click', (e) => {
                    e.stopPropagation();
                    toggle();
                });
            }

            if (closeBtn) {
                closeBtn.addEventListener('click', (e) => {
                    e.stopPropagation();
                    setState(card, false);
                    trigger.focus();
                });
            }

            // Desktop hover opens via CSS :hover; on mouseleave clear the
            // Escape dismissal so the next hover reopens.
            card.addEventListener('mouseleave', () => {
                card.classList.remove('is-dismissed');
            });
        });

        // Global Escape: hover-opened panel has no focus, so listen on the
        // document. Card opened by click/keyboard (.is-open) -> close and
        // restore focus; card opened by hover -> add .is-dismissed to force
        // close despite :hover.
        document.addEventListener('keydown', (e) => {
            if (e.key !== 'Escape') return;
            cards.forEach(card => {
                if (card.classList.contains('is-open')) {
                    setState(card, false);
                    const trigger = card.querySelector(TRIGGER_SELECTOR);
                    if (trigger && card.contains(document.activeElement)) trigger.focus();
                } else if (card.matches(':hover')) {
                    card.classList.add('is-dismissed');
                }
            });
        });
    },
};

export default PanelCard;
